import { useRef, useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import {
  parseCsv,
  validateAndResolveRows,
  type ImportContext,
  type ParsedRow,
  type ImportSummary,
} from '@/lib/csvImport';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Loader2,
  Upload,
  AlertCircle,
  CheckCircle2,
  Download,
  Info,
} from 'lucide-react';

// ---- Types ------------------------------------------------------------------

type Step = 'upload' | 'preview' | 'importing' | 'done';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful import so the parent can refresh its pupil list */
  onComplete: () => void;
}

// ---- Helpers ----------------------------------------------------------------

const TEMPLATE_HEADERS = 'first_name,last_name,year_group,class_name,anonymisation,archived';
const TEMPLATE_EXAMPLE = 'Emma,Johnson,Year 1,1A,,';

function downloadTemplate() {
  const csv = `${TEMPLATE_HEADERS}\n${TEMPLATE_EXAMPLE}\n`;
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'pupils_import_template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

function rowStatusBadge(row: ParsedRow) {
  if (row.imported) {
    return <Badge className="bg-success text-success-foreground text-xs hover:bg-success/90">Imported</Badge>;
  }
  if (row.willCreateClass && row.status === 'valid') {
    return <Badge className="bg-warning/10 text-warning-foreground border-warning/40 text-xs hover:bg-warning/10 border">New class</Badge>;
  }
  if (row.status === 'error') {
    return <Badge variant="destructive" className="text-xs">Error</Badge>;
  }
  if (row.status === 'duplicate') {
    return <Badge variant="outline" className="text-xs text-warning border-warning/60">Skipped</Badge>;
  }
  return <Badge variant="secondary" className="text-xs text-success bg-success/10 hover:bg-success/10">Ready</Badge>;
}

function duplicateReason(row: ParsedRow): string {
  if (row.isDuplicateInCsv && row.isDuplicateInDb) return 'Duplicate in file and already in database';
  if (row.isDuplicateInCsv) return 'Duplicate within file';
  if (row.isDuplicateInDb) return 'Already exists in this class';
  return '';
}

// ---- Component --------------------------------------------------------------

export default function PupilImportDialog({ open, onOpenChange, onComplete }: Props) {
  const { user, profile, hasRole, schoolPlan } = useAuth();
  const isAdmin = hasRole('school_admin');

  const [step, setStep] = useState<Step>('upload');
  const [parseError, setParseError] = useState<string | null>(null);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [summary, setSummary] = useState<ImportSummary | null>(null);

  // Context loaded eagerly when dialog opens
  const [context, setContext] = useState<ImportContext | null>(null);
  const [loadingContext, setLoadingContext] = useState(false);
  const [contextError, setContextError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // ---- Load context when dialog opens ----------------------------------------

  const buildContext = useCallback(async (): Promise<ImportContext | null> => {
    if (!user) return null;

    const schoolId = profile?.school_id ?? null;

    let availableYearGroups: ImportContext['availableYearGroups'] = [];
    let availableClasses: ImportContext['availableClasses'] = [];

    if (schoolId && isAdmin && schoolPlan !== 'individual') {
      const [ygsResult, classesResult] = await Promise.all([
        supabase
          .from('year_groups')
          .select('id, name, key_stages!inner(school_id)')
          .eq('key_stages.school_id', schoolId)
          .order('name'),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        supabase.from('classes').select('id, name, year_group_id, year_groups(id, name)') as any,
      ]);

      if (ygsResult.error) throw new Error(ygsResult.error.message);

      availableYearGroups = (ygsResult.data ?? []).map((y) => ({ id: y.id, name: y.name }));
      const ygIds = new Set(availableYearGroups.map((y) => y.id));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      availableClasses = ((classesResult.data ?? []) as any[])
        .filter((c) => c.year_group_id && ygIds.has(c.year_group_id))
        .map((c) => ({
          id: c.id,
          name: c.name,
          yearGroupId: c.year_group_id ?? null,
          yearGroupName: c.year_groups?.name ?? '',
        }));
    } else {
      const tcResult = await supabase
        .from('teacher_classes')
        .select('class_id, classes(id, name, year_group_id, year_groups(id, name))')
        .eq('user_id', user.id);

      if (tcResult.error) throw new Error(tcResult.error.message);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const raw = (tcResult.data ?? []) as any[];
      const ygMap = new Map<string, string>();

      availableClasses = raw
        .map((row) => {
          const cls = row.classes;
          if (cls?.year_groups) ygMap.set(cls.year_groups.id, cls.year_groups.name);
          return {
            id: cls?.id ?? '',
            name: cls?.name ?? '',
            yearGroupId: cls?.year_group_id ?? null,
            yearGroupName: cls?.year_groups?.name ?? '',
          };
        })
        .filter((c) => c.id);

      availableYearGroups = Array.from(ygMap.entries()).map(([id, name]) => ({ id, name }));
    }

    const pupilsResult = await supabase
      .from('pupils')
      .select('first_name, last_name, class_id')
      .eq('is_archived', false);

    if (pupilsResult.error) throw new Error(pupilsResult.error.message);

    return {
      scope: schoolPlan === 'individual' ? 'individual' : 'school',
      schoolId,
      userId: user.id,
      availableClasses,
      availableYearGroups,
      existingPupils: (pupilsResult.data ?? []).map((p) => ({
        first_name: p.first_name,
        last_name: p.last_name,
        class_id: p.class_id,
      })),
    };
  }, [user, profile, hasRole, schoolPlan]);

  useEffect(() => {
    if (!open) return;
    setLoadingContext(true);
    setContextError(null);
    buildContext()
      .then((ctx) => setContext(ctx))
      .catch((err) => setContextError((err as Error).message ?? 'Failed to load school data.'))
      .finally(() => setLoadingContext(false));
  }, [open, buildContext]);

  // ---- Reset on close -------------------------------------------------------

  const handleOpenChange = (val: boolean) => {
    if (!val) {
      setStep('upload');
      setParseError(null);
      setRows([]);
      setSummary(null);
      setContextError(null);
      setContext(null);
    }
    onOpenChange(val);
  };

  // ---- File upload ----------------------------------------------------------

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (fileInputRef.current) fileInputRef.current.value = '';

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setParseError('Please upload a .csv file.');
      return;
    }
    if (!context) {
      setParseError('School data not loaded yet. Please wait and try again.');
      return;
    }

    setParseError(null);

    try {
      const text = await file.text();
      const { rows: rawRows, parseError: err } = parseCsv(text);

      if (err) { setParseError(err); return; }
      if (rawRows.length === 0) { setParseError('No data rows found in the file.'); return; }

      setRows(validateAndResolveRows(rawRows, context));
      setStep('preview');
    } catch (err) {
      setParseError((err as Error).message ?? 'Unexpected error reading file.');
    }
  };

  // ---- Import ---------------------------------------------------------------

  const handleImport = async () => {
    const validRows = rows.filter((r) => r.status === 'valid');
    if (validRows.length === 0) return;

    setStep('importing');

    let imported = 0;
    let failed = 0;
    const updatedRows = [...rows];

    // Dedupe: track classes created this run
    // Key: `${yearGroupId}::${trimmedClassName.toLowerCase()}` → classId
    const createdClassMap = new Map<string, string>();

    for (let i = 0; i < updatedRows.length; i++) {
      const row = updatedRows[i];
      if (row.status !== 'valid') continue;

      let classId = row.resolvedClassId;

      // Individual plan: create class if needed
      if (!classId && row.willCreateClass && row.resolvedYearGroupId && context) {
        const mapKey = `${row.resolvedYearGroupId}::${row.class_name.trim().toLowerCase()}`;

        if (createdClassMap.has(mapKey)) {
          classId = createdClassMap.get(mapKey)!;
        } else {
          // Create the class
          const { data: cls, error: clsError } = await supabase
            .from('classes')
            .insert({ name: row.class_name.trim(), year_group_id: row.resolvedYearGroupId })
            .select('id')
            .single();

          if (clsError || !cls) {
            updatedRows[i] = {
              ...row, status: 'error',
              errors: [`Failed to create class "${row.class_name}": ${clsError?.message ?? 'unknown error'}`],
            };
            failed++;
            continue;
          }

          classId = cls.id;
          createdClassMap.set(mapKey, classId);

          // Link teacher to new class (once per class per run)
          if (user) {
            const { error: tcInsertError } = await supabase
              .from('teacher_classes')
              .insert({ user_id: user.id, class_id: classId });
            if (tcInsertError && tcInsertError.code !== '23505') {
              // Roll back the class we just created
              await supabase.from('classes').delete().eq('id', classId);
              // Remove from map so next row won't reuse this failed class
              createdClassMap.delete(mapKey);
              updatedRows[i] = {
                ...row, status: 'error',
                errors: [`Failed to link class "${row.class_name}": ${tcInsertError.message}`],
              };
              failed++;
              continue;
            }
          }
        }
      }

      if (!classId) {
        updatedRows[i] = { ...row, status: 'error', errors: ['Class not resolved.'] };
        failed++;
        continue;
      }

      const { error } = await supabase.from('pupils').insert({
        first_name: row.first_name,
        last_name: row.last_name,
        class_id: classId,
        anonymisation: row.anonymisation,
        is_archived: row.is_archived,
      });

      if (error) {
        updatedRows[i] = { ...row, status: 'error', errors: [error.message] };
        failed++;
      } else {
        updatedRows[i] = { ...row, imported: true };
        imported++;
      }
    }

    setRows(updatedRows);
    setSummary({
      total: updatedRows.length,
      imported,
      skipped: updatedRows.filter((r) => r.status === 'duplicate').length,
      failed,
    });
    setStep('done');
    if (imported > 0) onComplete();
  };

  // ---- Derived counts -------------------------------------------------------

  const validCount = rows.filter((r) => r.status === 'valid').length;
  const errorCount = rows.filter((r) => r.status === 'error').length;
  const dupCount = rows.filter((r) => r.status === 'duplicate').length;

  // ---- Class reference (grouped by year group) ------------------------------

  const classesByYearGroup = context
    ? context.availableYearGroups
        .map((yg) => ({
          yearGroup: yg,
          classes: context.availableClasses
            .filter((c) => c.yearGroupId === yg.id)
            .sort((a, b) => a.name.localeCompare(b.name)),
        }))
        .filter((g) => g.classes.length > 0)
    : [];

  // ---- Render ---------------------------------------------------------------

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Import Pupils from CSV</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">

        {/* ---- Upload step ---- */}
        {step === 'upload' && (
          <div className="space-y-4 py-2">

            {/* Guidance callout */}
            <div className="flex gap-2.5 rounded-lg border border-info/30 bg-info/10 px-3 py-2.5 text-sm text-info-foreground">
              <Info className="h-4 w-4 mt-0.5 shrink-0 text-info" />
              <div className="space-y-1">
                <p className="font-medium">Before you upload</p>
                <ul className="list-disc list-inside space-y-0.5 text-xs text-info-foreground/80">
                  {context?.scope === 'individual' ? (
                    <li>Classes in your CSV that don't exist yet will be <strong>created automatically</strong></li>
                  ) : (
                    <li>Classes must already exist — add them in <strong>School Setup → Classes</strong> first</li>
                  )}
                  <li><code className="bg-info/20 px-1 rounded">class_name</code> must match an existing class name exactly</li>
                  <li>Required columns: <code className="bg-info/20 px-1 rounded">first_name</code>, <code className="bg-info/20 px-1 rounded">last_name</code>, <code className="bg-info/20 px-1 rounded">year_group</code>, <code className="bg-info/20 px-1 rounded">class_name</code></li>
                  <li>Optional: <code className="bg-info/20 px-1 rounded">anonymisation</code> (full_name / first_initial / initials_only), <code className="bg-info/20 px-1 rounded">archived</code> (true/false)</li>
                </ul>
              </div>
            </div>

            {/* Valid class names */}
            {loadingContext && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-1">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading your classes…
              </div>
            )}
            {!loadingContext && classesByYearGroup.length > 0 && (
              <div className="rounded-lg border bg-muted/40 p-3 space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Your classes — use these exact names in class_name
                </p>
                <div className="space-y-1.5">
                  {classesByYearGroup.map(({ yearGroup, classes }) => (
                    <div key={yearGroup.id} className="flex flex-wrap items-center gap-1.5">
                      <span className="text-xs text-muted-foreground w-16 shrink-0">{yearGroup.name}</span>
                      {classes.map((cls) => (
                        <Badge key={cls.id} variant="secondary" className="text-xs font-mono">
                          {cls.name}
                        </Badge>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!loadingContext && context && classesByYearGroup.length === 0 && context.scope !== 'individual' && (
              <div className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2.5 text-sm text-warning-foreground">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>No classes found. Go to <strong>School Setup → Classes</strong> to add classes before importing pupils.</span>
              </div>
            )}

            {/* Errors */}
            {(parseError || contextError) && (
              <div className="flex items-start gap-2 rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{parseError ?? contextError}</span>
              </div>
            )}

            {/* Upload zone */}
            <label
              htmlFor="csv-upload"
              className={`flex flex-col items-center gap-3 cursor-pointer rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors ${
                loadingContext || !context
                  ? 'border-border opacity-50 cursor-not-allowed'
                  : 'border-border hover:border-primary'
              }`}
            >
              <Upload className="h-7 w-7 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Click to choose a CSV file</p>
                <p className="text-xs text-muted-foreground mt-0.5">.csv files only</p>
              </div>
              <input
                id="csv-upload"
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                disabled={loadingContext || !context}
                onChange={handleFileChange}
              />
            </label>
          </div>
        )}

        {/* ---- Preview step ---- */}
        {step === 'preview' && (
          <div className="space-y-3 py-2">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-muted-foreground">{rows.length} rows —</span>
              <Badge variant="secondary" className="text-xs text-success bg-success/10 hover:bg-success/10">
                {validCount} ready
              </Badge>
              {dupCount > 0 && (
                <Badge variant="outline" className="text-xs text-warning border-warning/60">
                  {dupCount} skipped
                </Badge>
              )}
              {errorCount > 0 && (
                <Badge variant="destructive" className="text-xs">
                  {errorCount} error{errorCount !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>

            <ScrollArea className="h-72 rounded-md border">
              <Table className="table-fixed w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8 text-xs">#</TableHead>
                    <TableHead className="w-36 text-xs">Name</TableHead>
                    <TableHead className="w-32 text-xs">Year / Class</TableHead>
                    <TableHead className="w-20 text-xs">Status</TableHead>
                    <TableHead className="text-xs">Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow
                      key={row.rowNumber}
                      className={
                        row.status === 'error'
                          ? 'bg-destructive/5'
                          : row.status === 'duplicate'
                            ? 'bg-warning/5'
                            : undefined
                      }
                    >
                      <TableCell className="text-muted-foreground text-xs py-2">{row.rowNumber}</TableCell>
                      <TableCell className="font-medium text-sm py-2 truncate max-w-0">
                        {row.first_name} {row.last_name}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground py-2 truncate max-w-0">
                        {row.year_group} / {row.class_name}
                      </TableCell>
                      <TableCell className="py-2">{rowStatusBadge(row)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground py-2">
                        {row.status === 'error' && row.errors.join('. ')}
                        {row.status === 'duplicate' && duplicateReason(row)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>

            {validCount === 0 && (
              <p className="text-sm text-muted-foreground text-center py-1">
                No rows are ready to import. Fix the errors and re-upload.
              </p>
            )}
          </div>
        )}

        {/* ---- Importing step ---- */}
        {step === 'importing' && (
          <div className="flex flex-col items-center gap-3 py-12 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-sm">Importing pupils…</p>
          </div>
        )}

        {/* ---- Done step ---- */}
        {step === 'done' && summary && (
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-success" />
              <span className="font-medium">Import complete</span>
            </div>

            <div className="grid grid-cols-4 gap-3 text-center">
              {[
                { label: 'Total rows', value: summary.total, color: '' },
                { label: 'Imported', value: summary.imported, color: 'text-success' },
                { label: 'Skipped', value: summary.skipped, color: summary.skipped > 0 ? 'text-warning' : '' },
                { label: 'Failed', value: summary.failed, color: summary.failed > 0 ? 'text-destructive' : '' },
              ].map(({ label, value, color }) => (
                <div key={label} className="rounded-lg border p-3">
                  <p className={`text-2xl font-bold ${color}`}>{value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{label}</p>
                </div>
              ))}
            </div>

            {(summary.failed > 0 || summary.skipped > 0) && (
              <ScrollArea className="h-44 rounded-md border">
                <Table className="table-fixed w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8 text-xs">#</TableHead>
                      <TableHead className="w-36 text-xs">Name</TableHead>
                      <TableHead className="w-20 text-xs">Status</TableHead>
                      <TableHead className="text-xs">Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows
                      .filter((r) => r.status === 'error' || r.status === 'duplicate')
                      .map((row) => (
                        <TableRow key={row.rowNumber}>
                          <TableCell className="text-xs text-muted-foreground py-2">{row.rowNumber}</TableCell>
                          <TableCell className="text-sm font-medium py-2 truncate max-w-0">
                            {row.first_name} {row.last_name}
                          </TableCell>
                          <TableCell className="py-2">{rowStatusBadge(row)}</TableCell>
                          <TableCell className="text-xs text-muted-foreground py-2">
                            {row.status === 'error' ? row.errors.join('. ') : duplicateReason(row)}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            )}
          </div>
        )}

        </div>{/* end scrollable area */}

        {/* ---- Footer ---- */}
        <DialogFooter className="pt-2 border-t mt-2">
          {step === 'upload' && (
            <>
              <Button variant="outline" size="sm" onClick={downloadTemplate}>
                <Download className="h-3.5 w-3.5 mr-1.5" />
                Download template
              </Button>
              <div className="flex-1" />
              <Button variant="ghost" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
            </>
          )}

          {step === 'preview' && (
            <>
              <Button variant="ghost" onClick={() => setStep('upload')}>
                Back
              </Button>
              <Button onClick={handleImport} disabled={validCount === 0}>
                Import {validCount > 0 ? `${validCount} pupil${validCount !== 1 ? 's' : ''}` : '(no valid rows)'}
              </Button>
            </>
          )}

          {step === 'done' && (
            <Button onClick={() => handleOpenChange(false)}>Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
